/**
 * Local-date helpers.
 *
 * `new Date().toISOString().slice(0, 10)` returns the date in UTC, not in
 * the user's local timezone. For users west of UTC (Americas) this can
 * return tomorrow's date during the evening hours; for users east of UTC
 * it can return yesterday's date during early morning. Either way,
 * "what date is it for the user right now?" gets answered wrong.
 *
 * The bug is silent — code that reads `today` for comparisons against
 * locally-meaningful dates (reporting cadences, deadlines, "due by") will
 * silently include rows the user thinks are due tomorrow, or exclude rows
 * the user thinks are due today. Skip caught this on Pilot Cities Phase
 * showing 8 expected periods when only 7 were due locally.
 *
 * Use `todayIso()` everywhere "what's today in the user's timezone" is
 * the right question. Use `toLocalIsoDate(d)` to convert any Date object
 * to its local YYYY-MM-DD without UTC drift.
 *
 * NOTE: this is local-time-of-this-browser only. Server timestamps still
 * arrive in UTC and need their own handling if they need to be compared
 * to a user-local date.
 */

/**
 * Format a Date instance as YYYY-MM-DD using local-timezone components.
 * Replaces `d.toISOString().slice(0, 10)` which uses UTC components.
 */
export function toLocalIsoDate(d: Date): string {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
}

/**
 * Today's date in the user's local timezone, formatted as YYYY-MM-DD.
 * Drop-in replacement for `new Date().toISOString().slice(0, 10)`.
 */
export function todayIso(): string {
  return toLocalIsoDate(new Date());
}
